import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale, Shield, AlertTriangle, Users, FileText, RefreshCw, Mail } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#1a1f2e]/95 backdrop-blur-lg border-b border-white/5 z-50">
        <div className="max-w-[1280px] mx-auto h-full flex items-center justify-between px-4 lg:px-8">
          <Link to="/" className="text-xl font-bold text-white tracking-tight">Casino<span className="text-[#4d7cfe]">IQ</span></Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#a0a8b8] hover:text-white transition-colors">Log In</Link>
            <Link to="/signup" className="px-5 py-2 rounded-lg bg-[#4d7cfe] text-white text-sm font-semibold hover:brightness-110 transition-all">Get Started</Link>
          </div>
        </div>
      </header>

      <div className="pt-28 pb-20 px-4" data-testid="terms-page">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a0a8b8] hover:text-[#4d7cfe] transition-colors mb-8" data-testid="terms-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" style={{ letterSpacing: '-0.02em' }} data-testid="terms-heading">Terms of Service</h1>
          <p className="text-[#a0a8b8] text-sm mb-12">Last updated: February 2026</p>

          <div className="space-y-10">
            {/* Section 1 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><Scale className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">1. Acceptance of Terms</h2>
              </div>
              <div className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-3">
                <p>By accessing CasinoIQ, you agree to these terms.</p>
                <p>You must be 18+ to use this platform.</p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center"><FileText className="h-5 w-5 text-[#8b5cf6]" /></div>
                <h2 className="text-xl font-semibold text-white">2. Service Description</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                CasinoIQ provides educational gambling tools for informational purposes only. Tools do not constitute financial or gambling advice.
              </p>
            </section>

            {/* Section 3 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#10b981]/10 flex items-center justify-center"><Users className="h-5 w-5 text-[#10b981]" /></div>
                <h2 className="text-xl font-semibold text-white">3. User Accounts</h2>
              </div>
              <ul className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-2 list-disc list-inside">
                <li>You are responsible for account security</li>
                <li>One account per person</li>
                <li>You agree to receive promotional emails from CasinoIQ and casino partners upon signup</li>
                <li>Unsubscribe anytime via email settings</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#f59e0b]/10 flex items-center justify-center"><Shield className="h-5 w-5 text-[#f59e0b]" /></div>
                <h2 className="text-xl font-semibold text-white">4. Affiliate Disclosure</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                CasinoIQ contains affiliate links to casino partners. We may earn commission when you sign up at partner casinos. This does not affect tool accuracy or editorial independence.
              </p>
            </section>

            {/* Section 5 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10 border-l-4 border-l-[#10b981]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#10b981]/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-[#10b981]" /></div>
                <h2 className="text-xl font-semibold text-white">5. Responsible Gambling</h2>
              </div>
              <div className="text-[#a0a8b8] text-[15px] leading-relaxed space-y-3">
                <p>These tools are for entertainment and education only. If you have a gambling problem, contact:</p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <a href="https://www.ncpgambling.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#10b981]/10 text-[#10b981] text-sm font-medium hover:bg-[#10b981]/20 transition-colors">
                    National Gambling Helpline
                  </a>
                  <a href="https://www.begambleaware.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#10b981]/10 text-[#10b981] text-sm font-medium hover:bg-[#10b981]/20 transition-colors">
                    BeGambleAware.org
                  </a>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#ef4444]/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-[#ef4444]" /></div>
                <h2 className="text-xl font-semibold text-white">6. Disclaimer</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                CasinoIQ does not guarantee gambling outcomes. All tools provide estimates based on mathematical models. Actual results will vary.
              </p>
            </section>

            {/* Section 7 */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><RefreshCw className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">7. Changes to Terms</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px] leading-relaxed">
                We may update these terms. Continued use constitutes acceptance of the updated terms.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-[#20263a] rounded-xl p-8 border border-[#4d7cfe]/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#4d7cfe]" /></div>
                <h2 className="text-xl font-semibold text-white">Contact</h2>
              </div>
              <p className="text-[#a0a8b8] text-[15px]">
                Questions about these terms? Reach us at <a href="mailto:support@casinoiq.com" className="text-[#4d7cfe] hover:underline">support@casinoiq.com</a>
              </p>
            </section>
          </div>

          <div className="mt-12 text-center">
            <Link to="/privacy" className="text-sm text-[#4d7cfe] hover:underline" data-testid="terms-to-privacy">View our Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
